/**
 * Auto-actualización del cliente al detectar deploy nuevo.
 *
 * Estrategia:
 *   - Backend `/api/version` devuelve la mtime de `server.py` dentro del contenedor.
 *     Cada `docker compose build` la refresca.
 *   - Cliente guarda en localStorage la versión que vio la última vez.
 *   - Al iniciar la app, y cada VERSION_CHECK_INTERVAL_MS, compara contra el server.
 *     Si difiere → limpia IndexedDB + Cache API + desregistra Service Workers
 *     y recarga la página, conservando el token de sesión.
 *
 * Esto evita que gestores en campo o coordinadores en oficina queden viendo
 * versiones obsoletas tras un despliegue.
 */

const STORAGE_KEY = 'app_version';
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos
let _intervalId = null;
let _resetting = false;

const API = process.env.REACT_APP_BACKEND_URL || '';

async function fetchServerVersion() {
  try {
    const res = await fetch(`${API}/api/version`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version || null;
  } catch (err) {
    // Sin conexión / backend caído: no fuerza nada
    return null;
  }
}

async function clearLocalCachesAndReload() {
  if (_resetting) return;
  _resetting = true;
  try {
    // Conservar credenciales para no desloguear
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    // Y conservar la versión que dispará el reset, para no entrar en loop de
    // "primer arranque" tras el reload (G4: localStorage.clear más adelante
    // borra el STORAGE_KEY recién guardado por checkVersionOnce).
    const savedAppVersion = localStorage.getItem(STORAGE_KEY);

    // 1. IndexedDB
    if (typeof indexedDB !== 'undefined') {
      try {
        if (indexedDB.databases) {
          const dbs = await indexedDB.databases();
          for (const db of dbs) {
            if (db?.name) {
              await new Promise((r) => {
                const req = indexedDB.deleteDatabase(db.name);
                req.onsuccess = req.onerror = req.onblocked = r;
              });
            }
          }
        } else {
          // Fallback: nombres conocidos
          ['asomunicipios_offline_v2', 'asomunicipios_offline'].forEach((n) => indexedDB.deleteDatabase(n));
        }
      } catch (_) { /* ignorar */ }
    }

    // 2. Cache API
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch (_) { /* ignorar */ }
    }

    // 3. Service Workers
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch (_) { /* ignorar */ }
    }

    // 4. Storage (preservando sesión y la versión nueva)
    try {
      localStorage.clear();
      sessionStorage.clear();
      if (token) localStorage.setItem('token', token);
      if (userId) localStorage.setItem('userId', userId);
      // Restaurar la versión nueva para que el siguiente arranque la encuentre
      // y no caiga al branch de "primer arranque" en checkVersionOnce.
      if (savedAppVersion) localStorage.setItem(STORAGE_KEY, savedAppVersion);
    } catch (_) { /* ignorar */ }

    // 5. Reload (saltándose caché)
    window.location.reload();
  } catch (err) {
    console.warn('[VersionCheck] Error reseteando caché:', err);
    _resetting = false;
  }
}

async function checkVersionOnce() {
  const remote = await fetchServerVersion();
  if (!remote) return; // backend no respondió, no hacer nada

  const local = localStorage.getItem(STORAGE_KEY);
  if (!local) {
    // Primer arranque: guardar y seguir
    localStorage.setItem(STORAGE_KEY, remote);
    return;
  }

  if (local !== remote) {
    console.info('[VersionCheck] Despliegue nuevo detectado:', local, '→', remote);
    // Guardar la nueva versión ANTES de recargar para no entrar en bucle
    localStorage.setItem(STORAGE_KEY, remote);
    await clearLocalCachesAndReload();
  }
}

/** Activa la verificación: una vez al cargar y luego cada 5 min. Idempotente. */
export function startVersionCheck() {
  // Verificación inmediata al arranque
  checkVersionOnce();
  // Periódica
  if (_intervalId) clearInterval(_intervalId);
  _intervalId = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    checkVersionOnce();
  }, VERSION_CHECK_INTERVAL_MS);

  // También al volver al foco de la pestaña (típico al reanudar trabajo)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkVersionOnce();
    });
  }
}

export function stopVersionCheck() {
  if (_intervalId) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}
